import { ArrowLeft } from 'lucide-react';
import { href } from '../app/router';
import { course } from '../content';
import { DESIRED_RETENTION } from '../lib/scheduler';

/** The method and its evidence, where the content and audio come from, and privacy. */
export function About() {
  return (
    <main className="page" id="main">
      <a href={href('/settings')} className="btn btn-ghost" style={{ alignSelf: 'flex-start', marginLeft: -12 }}>
        <ArrowLeft size={18} aria-hidden="true" /> Settings
      </a>
      <header className="stack">
        <p className="eyebrow">How this works</p>
        <h1 className="title">Phrases first, out loud, spaced out</h1>
      </header>

      <section className="stack">
        <h2 className="section-title">The method</h2>
        <ul className="stack" style={{ paddingLeft: '1.1rem', margin: 0 }}>
          <li>
            <strong>Whole phrases for real situations.</strong> Much of everyday speech is ready-made phrases, and learners who have
            a store of them speak more fluently. Every phrase here belongs to a real moment: a café, a shop, a pharmacy, the school gate.
          </li>
          <li>
            <strong>The other half of the conversation.</strong> Beginners struggle most with what they hear back, so you also learn
            the other person's lines (the waiter's, the pharmacist's) and what to say when you don't understand.
          </li>
          <li>
            <strong>Say it aloud, from memory.</strong> Recalling beats re-reading, and recalling out loud beats recalling silently.
            You try first, then hear the model next to your own recording.
          </li>
          <li>
            <strong>Short "why" notes, after you've said it.</strong> Brief explanations that compare Greek with English work, and
            they fold away once you know the phrase.
          </li>
          <li>
            <strong>Spaced repetition with FSRS.</strong> Each phrase comes back just before you'd forget it, aiming for{' '}
            {Math.round(DESIRED_RETENTION * 100)}% recall. A phrase you say has two cards, saying it and understanding it; the second
            starts the next day so one doesn't give the other away.
          </li>
          <li>
            <strong>Small daily doses.</strong> Five new phrases a day keeps reviews short; new phrases pause if reviews pile up. A
            missed card comes back once later in the session, never drilled to death.
          </li>
          <li>
            <strong>Role-play, then faster.</strong> Each lesson ends with its scene, then a faster round with no English, to build
            fluency as well as accuracy.
          </li>
          <li>
            <strong>Stories in easy Greek.</strong> Short cartoons of the Greek myths show their words first, then tell the story
            with them, so you can follow it by ear. A new episode opens every two lessons, in order.
          </li>
          <li>
            <strong>Calm motivation.</strong> Progress is what you can now do. A weekly target instead of a streak, and no points,
            hearts or leaderboards.
          </li>
        </ul>
      </section>

      <section className="stack">
        <h2 className="section-title">Where the content comes from</h2>
        <p>
          The {course.items.length} phrases, their word-by-word glosses and notes were drafted with AI and passed automatic checks.{' '}
          <strong>They are checked by AI, not by a native speaker.</strong> Flag anything that looks wrong.
        </p>
        <p>
          The audio is synthetic speech ({course.audio.tts}), and each person speaks with their own voice: you with a woman's, the people you talk to with a man's or another woman's. Every recording was transcribed automatically and
          compared with the text; {course.audio.excludedTakes.length} takes that failed the check ({course.audio.screening}) were
          left out. That check can't fully judge stress or naturalness, so flag anything that sounds off.
        </p>
        <p>
          The illustrations are AI-generated with Meta's Muse Image from a written brief for each picture, and each one was checked by AI
          against its brief: the right people, no writing, gestures that read the same in Greece.
        </p>
        <p>
          The stories are drawn in code and narrated with synthetic speech; every line was transcribed automatically through the
          finished sound track and compared with its text. Their music and sounds are synthesised too.
        </p>
        <p className="small muted">Content version {course.contentVersion}.</p>
      </section>

      <section className="stack">
        <h2 className="section-title">Privacy</h2>
        <p>
          The app runs entirely in your browser and works offline. Your progress is stored on this device only. Recordings stay in
          memory, are never uploaded, and are discarded when you move to the next card. There are no accounts, analytics or ads.
        </p>
      </section>

      <section className="stack">
        <h2 className="section-title">Credits</h2>
        <p className="small muted">
          Typeface Literata (SIL Open Font License). Scheduling by ts-fsrs (MIT). Icons by Lucide (ISC). Illustrations cut out
          with BiRefNet (MIT). Story animation with GSAP.
        </p>
      </section>
    </main>
  );
}
